import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'

const SELECTED_CLASSES = ['bg-red-50', 'text-red-600', 'font-medium']

// Date-range dropdown for the admin reports. Zero client-side date math:
// preset dates AND their computed compare ranges come from a
// server-rendered JSON map (Reporting::DateRange.preset_dates_map); custom
// dates are direct user picks. Compare math for custom primary ranges
// happens server-side on apply.
export default class extends Controller {
  static targets = [
    'panel',
    'form',
    'presetDates',
    'presetItem',
    'compareToggle',
    'compareTypeSection',
    'compareTypeItem',
    'compareDatesSection',
    'calendar',
    'startInput',
    'endInput',
    'compareStartInput',
    'compareEndInput',
  ]

  static values = {
    preset: String,
    startDate: String,
    endDate: String,
    compareEnabled: Boolean,
    compareType: String,
    compareStartDate: String,
    compareEndDate: String,
  }

  connect() {
    this.presetDates = JSON.parse(this.presetDatesTarget.textContent)
    this.initialState = this.snapshot()

    this.calendar = flatpickr(this.calendarTarget, {
      mode: 'range',
      inline: true,
      dateFormat: 'Y-m-d',
      maxDate: 'today',
      defaultDate: [this.startDateValue, this.endDateValue],
      onChange: (dates) => this.calendarChanged(dates),
    })
    this.startPicker = flatpickr(this.startInputTarget, this.primaryPickerOptions('startDateValue'))
    this.endPicker = flatpickr(this.endInputTarget, this.primaryPickerOptions('endDateValue'))
    this.comparePickers = [
      flatpickr(this.compareStartInputTarget, this.comparePickerOptions('compareStartDateValue')),
      flatpickr(this.compareEndInputTarget, this.comparePickerOptions('compareEndDateValue')),
    ]

    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target) && !event.target.closest('.flatpickr-calendar')) {
        this.close()
      }
    }
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.close()
    }
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onKeydown)

    this.render()
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onKeydown)
    ;[this.calendar, this.startPicker, this.endPicker, ...(this.comparePickers || [])].forEach(
      (picker) => picker && picker.destroy()
    )
    this.calendar = this.startPicker = this.endPicker = this.comparePickers = null
  }

  primaryPickerOptions(valueName) {
    return {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'm/d/Y',
      maxDate: 'today',
      onChange: (dates, dateStr) => {
        if (!dates[0]) return
        this[valueName] = dateStr
        this.presetValue = 'custom'
        this.calendar.setDate([this.startDateValue, this.endDateValue], false)
        this.render()
      },
    }
  }

  comparePickerOptions(valueName) {
    return {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'm/d/Y',
      maxDate: 'today',
      onChange: (dates, dateStr) => {
        if (!dates[0]) return
        if (this.compareTypeValue !== 'custom') {
          // Editing a computed range forks it into a custom compare range:
          // keep whatever both inputs currently display as the baseline.
          this.compareTypeValue = 'custom'
          this.compareStartDateValue = this.compareStartInputTarget.value
          this.compareEndDateValue = this.compareEndInputTarget.value
        }
        this[valueName] = dateStr
        this.render()
      },
    }
  }

  toggle() {
    this.panelTarget.classList.toggle('hidden')
  }

  close() {
    this.panelTarget.classList.add('hidden')
  }

  selectPreset(event) {
    const preset = event.currentTarget.dataset.preset
    this.presetValue = preset
    const entry = this.presetDates[preset]
    if (entry) {
      // Server-computed dates for this preset; no JS date math.
      this.startDateValue = entry.dates[0]
      this.endDateValue = entry.dates[1]
      this.calendar.setDate(entry.dates, false)
    }
    // A custom compare range was picked against the old primary range;
    // carrying it onto a new preset would submit stale, unrelated dates.
    if (this.compareTypeValue === 'custom') {
      this.compareTypeValue = 'previous_period'
      this.compareStartDateValue = ''
      this.compareEndDateValue = ''
    }
    this.render()
  }

  calendarChanged(dates) {
    if (dates.length !== 2) return

    this.startDateValue = this.isoDate(dates[0])
    this.endDateValue = this.isoDate(dates[1])
    this.presetValue = 'custom'
    this.render()
  }

  toggleCompare(event) {
    this.compareEnabledValue = event.target.checked
    this.render()
  }

  selectCompareType(event) {
    this.compareTypeValue = event.currentTarget.dataset.compareType
    this.render()
  }

  apply(event) {
    event.preventDefault()
    this.setHiddenField('preset', this.presetValue)
    this.setHiddenField('start_date', this.startDateValue, this.presetValue === 'custom')
    this.setHiddenField('end_date', this.endDateValue, this.presetValue === 'custom')
    this.setHiddenField('compare', '1', this.compareEnabledValue)
    this.setHiddenField('compare_type', this.compareTypeValue, this.compareEnabledValue)
    const customCompare = this.compareEnabledValue && this.compareTypeValue === 'custom'
    this.setHiddenField('compare_start_date', this.compareStartInputTarget.value, customCompare)
    this.setHiddenField('compare_end_date', this.compareEndInputTarget.value, customCompare)

    this.close()
    this.formTarget.requestSubmit()
  }

  cancel(event) {
    event.preventDefault()
    this.restore(this.initialState)
    this.close()
  }

  // --- internal ---

  snapshot() {
    return {
      preset: this.presetValue,
      startDate: this.startDateValue,
      endDate: this.endDateValue,
      compareEnabled: this.compareEnabledValue,
      compareType: this.compareTypeValue,
      compareStartDate: this.compareStartDateValue,
      compareEndDate: this.compareEndDateValue,
    }
  }

  restore(state) {
    this.presetValue = state.preset
    this.startDateValue = state.startDate
    this.endDateValue = state.endDate
    this.compareEnabledValue = state.compareEnabled
    this.compareTypeValue = state.compareType
    this.compareStartDateValue = state.compareStartDate
    this.compareEndDateValue = state.compareEndDate

    this.calendar.setDate([this.startDateValue, this.endDateValue], false)
    this.compareToggleTarget.checked = this.compareEnabledValue
    this.render()
  }

  render() {
    this.highlight(this.presetItemTargets, 'preset', this.presetValue)
    this.highlight(this.compareTypeItemTargets, 'compareType', this.compareTypeValue)
    this.compareTypeSectionTarget.classList.toggle('hidden', !this.compareEnabledValue)
    this.compareDatesSectionTarget.classList.toggle('hidden', !this.compareEnabledValue)
    this.startPicker.setDate(this.startDateValue, false)
    this.endPicker.setDate(this.endDateValue, false)
    this.renderCompareInputs()
  }

  renderCompareInputs() {
    if (!this.compareEnabledValue) return

    if (this.compareTypeValue === 'custom') {
      this.setCompareInputs(this.compareStartDateValue, this.compareEndDateValue, 'MM/DD/YYYY')
      return
    }

    const entry = this.presetDates[this.presetValue]
    if (entry) {
      const [start, end] = entry.compare[this.compareTypeValue]
      this.setCompareInputs(start, end, 'MM/DD/YYYY')
    } else if (this.matchesInitialState() && this.initialState.compareStartDate) {
      // Custom primary range, computed compare type: the server already
      // computed this range on initial render — reuse it.
      this.setCompareInputs(this.initialState.compareStartDate, this.initialState.compareEndDate, 'MM/DD/YYYY')
    } else {
      // Custom primary range whose compare dates are unknowable without
      // date math — the server computes them on apply.
      this.setCompareInputs('', '', 'Computed on apply')
    }
  }

  setCompareInputs(start, end, placeholder) {
    this.comparePickers[0].setDate(start || '', false)
    this.comparePickers[1].setDate(end || '', false)
    // altInput is the visible element; the original input stays ISO for the server.
    this.comparePickers.forEach((picker) => {
      picker.altInput.placeholder = placeholder
    })
  }

  matchesInitialState() {
    return (
      this.presetValue === this.initialState.preset &&
      this.startDateValue === this.initialState.startDate &&
      this.endDateValue === this.initialState.endDate &&
      this.compareTypeValue === this.initialState.compareType
    )
  }

  highlight(items, dataKey, selectedValue) {
    items.forEach((item) => {
      const selected = item.dataset[dataKey] === selectedValue
      SELECTED_CLASSES.forEach((klass) => item.classList.toggle(klass, selected))
      item.classList.toggle('text-gray-700', !selected)
    })
  }

  setHiddenField(name, value, enabled = true) {
    const input = this.formTarget.querySelector(`input[name="${name}"]`)
    input.value = enabled ? value : ''
    input.disabled = !enabled
  }

  isoDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
}
